import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Map as MapIcon, Trophy, Bell, X } from "lucide-react";

// First-run guided tour for the Today page: spotlights Ask jeeo, the map, and check-ins.
const STEPS = [
  { testid: null, icon: Sparkles, title: "Welcome to jeeo", body: "Quick 20-second tour so you know where everything is. Let's go!" },
  { testid: "campus-companion", icon: Sparkles, title: "Ask jeeo", body: "Got a gap between classes? Tell jeeo how much time you have and it suggests exactly what to do." },
  { testid: "campus-map", icon: MapIcon, title: "Your campus map", body: "See walk, bus and bike times from where you are to your next class — and nearby dining, study spots and rec." },
  { testid: "home-reward-tracker", icon: Trophy, title: "Check in & earn", body: "Check in around campus to build streaks, earn points and unlock badges. Your rewards keep growing all year." },
  { testid: "notifications-bell", icon: Bell, title: "Your crew's activity", body: "This bell lights up when friends are studying nearby or heading to a game. That's the tour — enjoy jeeo!" },
];

const PAD = 8;

export default function OnboardingTour() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  // Start once, only on Today, after content has rendered.
  useEffect(() => {
    if (location.pathname !== "/") return;
    if (localStorage.getItem("jeeo_tour_done")) return;
    const t = setTimeout(() => setActive(true), 1800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const finish = useCallback(() => {
    localStorage.setItem("jeeo_tour_done", "1");
    setActive(false);
  }, []);

  const measure = useCallback((i) => {
    const s = STEPS[i];
    if (!s || !s.testid) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${s.testid}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 380);
  }, []);

  useEffect(() => {
    if (!active) return;
    measure(step);
    const t = setTimeout(() => measure(step), 800);
    return () => clearTimeout(t);
  }, [active, step, measure]);

  if (!active) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  // Tooltip placement: below the target if room, else above; centered when no target.
  let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
  if (rect) {
    const below = rect.top + rect.height + 220 < window.innerHeight;
    const top = below ? rect.top + rect.height + PAD + 10 : Math.max(12, rect.top - PAD - 200);
    cardStyle = { top, left: "50%", transform: "translateX(-50%)" };
  }

  return (
    <AnimatePresence>
      <motion.div
        key="tour" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="onboarding-tour" className="fixed inset-0 z-[3500]"
      >
        {/* Spotlight: a highlight box that dims everything else via a huge box-shadow */}
        {rect ? (
          <motion.div
            initial={false}
            animate={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="absolute rounded-2xl"
            style={{ boxShadow: "0 0 0 9999px rgba(9,9,11,0.78)", outline: "2px solid rgba(255,210,30,0.9)" }}
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(9,9,11,0.82)" }} />
        )}

        <div
          data-testid="tour-card"
          className="absolute w-[88vw] max-w-xs bg-zinc-900 text-white border border-white/12 rounded-2xl shadow-2xl p-4"
          style={cardStyle}
        >
          <button data-testid="tour-skip" onClick={finish} className="absolute top-3 right-3 text-zinc-400 hover:text-white">
            <X size={16} />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-8 rounded-lg bg-[#FFD21E]/15 text-[#FFD21E] flex items-center justify-center">
              <Icon size={16} />
            </span>
            <div className="text-sm font-semibold">{s.title}</div>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">{s.body}</p>

          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-[#FFD21E]" : "bg-white/25"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && !last && (
                <button data-testid="tour-skip-link" onClick={finish} className="text-xs text-zinc-400 hover:text-white">Skip</button>
              )}
              <button
                data-testid="tour-next"
                onClick={() => (last ? finish() : setStep((v) => v + 1))}
                className="text-xs font-bold bg-[#FFD21E] text-black rounded-lg px-3 py-1.5 hover:opacity-90 transition-opacity"
              >
                {last ? "Done" : step === 0 ? "Start tour" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
